import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Container from "layouts/container/Container";
import { decode } from "shared/utils/Utils"; // Utils에서 decode 함수 import
import { useSelector, useDispatch } from "react-redux";
import { getTokenPayload } from "shared/utils/Utils";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { requester } from "App";
import axios from "axios"; // axios 다시 추가
import { devLog } from "utils/logger";

function DoorAccessGateway() {
    const { id } = useParams(); // storeCode
    const [iotDeviceId, setIotDeviceId] = useState("");
    const [statusMessage, setStatusMessage] = useState("접근 처리 중입니다...");
    const [statusType, setStatusType] = useState("info"); // "success", "error", "info", "warning"
    const [iotLoading, setIotLoading] = useState(false);
    const [tokenCheckComplete, setTokenCheckComplete] = useState(false); // 전체 프로세스 완료 여부
    const [doorOpenSuccess, setDoorOpenSuccess] = useState(false); // 문 열기 성공 여부
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Redux에서 token 가져오기 (초기값은 null일 수 있음)
    const { token } = useSelector((state) => ({
        token: state.auth.token,
    }));
    
    // 웹뷰 체크 함수
    const isOurAppWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        // Android 또는 iOS 웹뷰 환경인지 확인
        return /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent) || /wv|android.*samsungbrowser\/(?!.*chrome)/i.test(userAgent);
    };
    
    // 창 닫기 함수
    const closeWebView = () => {
        if (isOurAppWebView()) {
            if (window.BRIDGE?.closeWebView) {
                window.BRIDGE.closeWebView();
            } else if (window.webkit?.messageHandlers?.BRIDGE) {
                window.webkit.messageHandlers.BRIDGE.postMessage('closeWebView');
            }
        } else {
            try {
                window.close(); // 일반 브라우저는 닫기 시도
            } catch (error) {
                devLog('브라우저에서 창을 닫을 수 없습니다.');
            }
        }
    };

    // [신규] 앱에서 토큰 가져오기 (Promise 기반)
    const getTokenFromApp = () => {
        return new Promise((resolve, reject) => {
            // 타임아웃 설정 (5초)
            const timeoutId = setTimeout(() => {
                if (window.handleTokenResponse) {
                    delete window.handleTokenResponse;
                }
                reject(new Error("토큰 요청 시간 초과"));
            }, 5000);

            // Android
            if (window.BRIDGE?.getToken) {
                const token = window.BRIDGE.getToken();
                if (token) {
                    clearTimeout(timeoutId);
                    resolve(token);
                }
                // 안드로이드에서 토큰을 바로 못가져오는 경우를 대비해, 실패를 reject하지 않고 타임아웃에 의존할 수 있음
            } 
            // iOS
            else if (window.webkit?.messageHandlers?.BRIDGE) {
                // iOS 네이티브에서 호출할 전역 콜백 함수
                window.handleTokenResponse = (token) => {
                    clearTimeout(timeoutId);
                    if (token) {
                        resolve(token);
                    } else {
                        reject(new Error("iOS에서 토큰을 가져오지 못했습니다."));
                    }
                    delete window.handleTokenResponse;
                };
                window.webkit.messageHandlers.BRIDGE.postMessage({ command: "getToken" });
            } 
            // 웹뷰 환경이 아님
            else {
                clearTimeout(timeoutId);
                reject(new Error("앱 웹뷰 환경이 아닙니다."));
            }
        });
    };
    
    // 앱 다운로드 페이지로 이동
    const handleAppDownload = () => {
        window.open("https://puffuapp.com/appDown", "_blank");
    }
    
    // 인증 페이지로 이동
    const navigateToAuth = () => {
        navigate("/memberAccess", { 
            state: { 
                type: "door",
                storeCode: id,
                iotId: iotDeviceId,
                returnPath: `/ws/${id}/door-access`
            } 
        });
    };
    
    // [수정] 서버에서 관리하는 Tuya 토큰 가져오기
    const fetchTuyaTokenFromServer = () => {
        setStatusMessage("IoT 시스템 연결 중...");
        setStatusType("info");
        setIotLoading(true);

        return new Promise((resolve, reject) => {
            requester.findbyTuyaToken((result) => {
                setIotLoading(false);
                if (result.code === 0 && result.data?.accessToken) {
                    devLog("서버에서 가져온 토큰 정보:", result.data);
                    resolve(result.data.accessToken);
                } else {
                    console.error("서버에서 토큰 가져오기 실패:", result);
                    reject(new Error("서버에서 IoT 토큰을 가져오지 못했습니다."));
                }
            });
        });
    };
    
    // [수정] 문 열기 함수 - 직접 Tuya API 호출
    const openDoorById = async (deviceId, userAuthToken, tuyaToken) => {
        if (!deviceId || !userAuthToken || !tuyaToken) {
            const missing = [!deviceId && "디바이스 ID", !userAuthToken && "사용자 토큰", !tuyaToken && "IoT 토큰"].filter(Boolean).join(', ');
            setStatusMessage(`${missing}이(가) 필요합니다.`);
            setStatusType("warning");
            return false;
        }

        try {
            // 1. 출입 기록 생성
            const tokenPayload = getTokenPayload(userAuthToken);
            if (tokenPayload?.userId) {
                setStatusMessage("출입 권한을 확인하는 중...");
                setStatusType("info");
                
                const storeEntryAuthCreateData = {
                    storeCode: id,
                    userId: tokenPayload.userId
                };
                
                const authResult = await new Promise((resolve) => {
                    requester.storeEntryAuthCreate(storeEntryAuthCreateData, (result) => {
                        if (result.code === 0) {
                            devLog("출입 권한 생성 성공:", result);
                            resolve(true);
                        } else {
                            console.error("출입 권한 생성 실패:", result);
                            setStatusMessage(`출입 권한 생성 실패: ${result.message}`);
                            setStatusType("error");
                            resolve(false);
                        }
                    });
                });

                if (!authResult) return false;
            } else {
                setStatusMessage("사용자 정보를 확인할 수 없습니다.");
                setStatusType("error");
                return false;
            }

            // 2. 문 열기 요청
            setIotLoading(true);
            setStatusMessage("문 열기 요청 중...");
        
            const region = tuyaToken.substring(0, 2);
            const baseUrlMap = {
                "EU": "https://px1.tuyaeu.com/homeassistant/",
                "AY": "https://px1.tuyacn.com/homeassistant/",
                "AZ": "https://px1.tuyaus.com/homeassistant/"
            };
            const baseUrl = baseUrlMap[region] || "https://px1.tuyaeu.com/homeassistant/";
            const url = `${baseUrl}skill`;
            
            devLog(`지역 감지: ${region}, API URL: ${url}`);
            
            const data = {
                header: { name: "turnOnOff", namespace: "control", payloadVersion: 1 },
                payload: { accessToken: tuyaToken, devId: deviceId, value: 1 }
            };
            
            devLog(`문 열기 요청 데이터:`, JSON.stringify(data));
            
            const response = await axios.post(url, data, { 
                headers: { "Content-Type": "application/json" }
            });
            
            devLog("문 열기 응답:", response.data);
            setIotLoading(false);
            
            if (response.data?.header?.code === "SUCCESS") {
                setStatusMessage("문이 열렸습니다! 잠시 후 닫힙니다.");
                setStatusType("success");
                setDoorOpenSuccess(true);
                
                setTimeout(() => {
                    if (isOurAppWebView()) {
                        closeWebView();
                    } else {
                        navigate(`/ws/${id}/mobile/main`, { state: { doorOpened: true } });
                    }
                }, 3000);
                
                return true;
            } else {
                const errorCode = response.data?.header?.code || "UNKNOWN_ERROR";
                const errorMessage = response.data?.header?.message || "알 수 없는 오류";
                
                console.error(`문 열기 실패 - 코드: ${errorCode}, 메시지: ${errorMessage}`);
                
                if (errorCode === "FrequentlyInvoke") {
                    setStatusMessage(`요청이 너무 빈번합니다. 잠시 후 다시 시도해주세요.`);
                } else if (errorCode === "TokenInvalid" || errorCode === "UserSessionTimeOut") {
                    setStatusMessage(`IoT 토큰이 만료되었습니다. 다시 시도해주세요.`);
                } else {
                    setStatusMessage(`문 열기 실패: [${errorCode}] ${errorMessage}`);
                }
                
                setStatusType("error");
                return false;
            }
        } catch (error) {
            console.error("문 열기 오류:", error);
            if (error.response) {
                setStatusMessage(`문 열기 오류: [${error.response.status}] ${JSON.stringify(error.response.data || {})}`);
            } else if (error.request) {
                setStatusMessage("서버에서 응답이 없습니다. 네트워크를 확인해주세요.");
            } else {
                setStatusMessage(`요청 오류: ${error.message || "알 수 없는 오류"}`);
            }
            setStatusType("error");
            setIotLoading(false);
            return false;
        }
    };
    
    // 닫기 버튼 핸들러
    const handleCloseButton = () => {
        closeWebView();
    };

    // IoT ID 서버에서 가져오기
    useEffect(() => {
        if (!id) {
            setStatusMessage("매장 정보가 없습니다.");
            setStatusType("error");
            return;
        }

        setStatusMessage("출입 정보를 가져오는 중입니다...");
        setStatusType("info");

        const data = { storeCode: id, doorType: "ENTRANCE" };
        requester.getStoreDoorIotData(data, (result) => {
            // 서버 응답에서 iotId 필드를 사용한다고 가정합니다. 실제 필드명에 맞게 수정이 필요할 수 있습니다.
            if (result.code === 0 && result.data?.iotId) {
                devLog("서버에서 IoT ID 획득 성공:", result.data.iotId);devLog("서버에서 IoT ID 획득 성공:", result.data.iotId);
                setIotDeviceId(result.data.iotId);
                setIotDeviceId(result.data.iotId);
            } else {
                console.error("IoT 데이터 가져오기 실패:", result);
                setStatusMessage(result.message || "출입 정보를 가져오는데 실패했습니다. QR코드를 다시 스캔해주세요.");
                setStatusType("error");
                setTokenCheckComplete(true); // 더 이상 자동 처리 방지
            }
        });
    }, [id]);
    
    // 뒤로가기 방지 및 QR 코드 만료 시간 설정
    useEffect(() => {
        // 페이지 첫 로드 시 이전 페이지로의 접근 차단을 위한 초기화
        window.history.pushState(null, null, window.location.href);
        
        // HTML5 History API를 이용한 뒤로가기 방지 패턴
        let blockBackCount = 0;
        const MAX_BLOCK_COUNT = 10;
        
        // 뒤로가기 클릭 감지 및 차단 함수
        const blockBack = (e) => {
            // 뒤로가기 시도 카운트 증가
            blockBackCount++;
            
            // 이전 페이지로 가는 것을 방지하고 현재 페이지 유지
            window.history.pushState(null, null, window.location.href);
            
            // 너무 많은 시도 시 경고 메시지 변경
            if (blockBackCount >= MAX_BLOCK_COUNT) {
                alert(`보안상의 이유로 뒤로가기가 제한되어 있습니다. (${blockBackCount}회 시도)`);
            } else {
                alert("보안상의 이유로 뒤로가기가 제한되어 있습니다.");
            }
            
            // 브라우저 닫기 제안 (너무 많은 시도 시)
            if (blockBackCount >= MAX_BLOCK_COUNT * 2) {
                if (window.confirm("페이지를 벗어나시려면 브라우저를 닫으시거나 새 탭을 여세요.")) {
                    try {
                        window.close();
                    } catch (err) {
                        alert("브라우저 보안 정책으로 인해 창을 닫을 수 없습니다. 수동으로 닫아주세요.");
                    }
                }
            }
        };
        
        // 브라우저 뒤로가기 버튼 클릭 감지를 위한 이벤트 리스너
        window.addEventListener('popstate', blockBack);
        
        // 페이지 새로고침/닫기 방지 (선택적)
        const preventRefresh = (e) => {
            e.preventDefault();
            e.returnValue = "이 페이지를 떠나시겠습니까?";
            return e.returnValue;
        };
        window.addEventListener('beforeunload', preventRefresh);
        
        // 하드웨어 뒤로가기 버튼 처리 (모바일)
        document.addEventListener('backbutton', (e) => {
            e.preventDefault();
            alert("보안상의 이유로 뒤로가기가 제한되어 있습니다.");
        }, false);
        
        // QR 코드 만료 시간 설정 (5분 후 만료)
        const expirationTime = 5 * 60 * 1000; // 5분
        const expirationTimer = setTimeout(() => {
            setStatusMessage("보안을 위해 QR 코드가 만료되었습니다. 새 QR 코드를 스캔해주세요.");
            setStatusType("error");
            setIotDeviceId("");
            setTokenCheckComplete(true); // 더 이상 자동 처리 방지
        }, expirationTime);
        
        // 클린업 함수
        return () => {
            window.removeEventListener('popstate', blockBack);
            window.removeEventListener('beforeunload', preventRefresh);
            document.removeEventListener('backbutton', () => {}, false);
            clearTimeout(expirationTimer);
            if (window.handleTokenResponse) {
                delete window.handleTokenResponse;
            }
        };
    }, []);
    
    // [수정] 자동 초기화 (토큰 → 바로 문열기)
    useEffect(() => {
        if (tokenCheckComplete || !iotDeviceId) {
            return;
        }

        const initializeAndOpenDoor = async () => {
            if (!isOurAppWebView()) {
                devLog("앱 웹뷰가 아닙니다. 인증 페이지로 바로 이동합니다.");
                setStatusMessage("인증이 필요합니다. 로그인 페이지로 이동합니다.");
                setStatusType("warning");
                setTimeout(() => navigateToAuth(), 1500);
                setTokenCheckComplete(true);
                return;
            }

            try {
                // 1. 앱에서 토큰 가져오기 (Promise 기반)
                setStatusMessage("앱에서 정보를 가져오는 중입니다...");
                let appToken = token; // Redux에 이미 토큰이 있을 수 있음

                if (!appToken) {
                    appToken = await getTokenFromApp();
                    dispatch(AuthReducer.actions.setToken(appToken));
                    devLog("앱에서 토큰 획득 성공");
                }
                
                // 2. 서버에서 Tuya 토큰 가져오기
                const tuyaToken = await fetchTuyaTokenFromServer();
                devLog("서버에서 IoT 토큰 획득 성공");
                
                // 3. 문 열기 시도
                await openDoorById(iotDeviceId, appToken, tuyaToken);

            } catch (error) {
                devLog("초기화 과정 오류:", error.message);
                setStatusMessage(error.message || "처리 중 오류가 발생했습니다. 인증 페이지로 이동합니다.");
                setStatusType("error");
                setTimeout(() => navigateToAuth(), 2000);
            } finally {
                setTokenCheckComplete(true);
            }
        };

        initializeAndOpenDoor();
    }, [iotDeviceId, tokenCheckComplete, token]); // 의존성 배열 관리
    
    // 상태에 따른 스타일 설정
    const getStatusStyle = () => {
        switch(statusType) {
            case "success":
                return { color: "#28a745", fontWeight: "bold", fontSize: "18px" };
            case "error":
                return { color: "#dc3545", fontWeight: "bold", fontSize: "18px" };
            case "warning":
                return { color: "#ffc107", fontWeight: "bold", fontSize: "18px" };
            default:
                return { color: "#007bff", fontWeight: "bold", fontSize: "18px" };
        }
    };

    return (
        <Container>
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '20px'
            }}>
                <h1 style={{ marginBottom: '30px', color: 'var(--main-color2)' }}>Door Access</h1>
                
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '20px 0',
                    padding: '15px',
                    borderRadius: '8px',
                    backgroundColor: statusType === 'success' ? '#d4edda' : 
                                    statusType === 'error' ? '#f8d7da' :
                                    statusType === 'warning' ? '#fff3cd' : '#d1ecf1',
                    width: '100%',
                    maxWidth: '400px'
                }}>
                    <p style={getStatusStyle()}>{statusMessage}</p>
                    
                    {/* IoT 로딩 중 표시 */}
                    {iotLoading && (
                        <div style={{
                            marginTop: '10px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                border: '3px solid #f3f3f3',
                                borderTop: '3px solid var(--main-color2)',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                        </div>
                    )}
                </div>
                
                {/* 로딩 애니메이션 스타일 */}
                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
                </style>
            </div>
        </Container>
    );
}

export default DoorAccessGateway;
